import React, { useState, useRef } from "react";
import { useTheme } from "../../context/ThemeContext";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { toPng } from "html-to-image";
import HeaderSection from "../../components/HeaderSection/HeaderSection";
import BottomSection from "../../components/BottomSection/BottomSection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel } from "@fortawesome/free-solid-svg-icons";

const ExcelToPdfConverter = () => {
    const [excelData, setExcelData] = useState([]);
    const { darkMode } = useTheme();
    const [fileName, setFileName] = useState("");
    const [isConverting, setIsConverting] = useState(false);
    const [error, setError] = useState("");
    const tableRef = useRef(null);

    const handleFileUpload = (e) => {
        setError("");
        const file = e.target.files[0];
        if (!file) return;

        setFileName(file.name.replace(/\.[^/.]+$/, "")); // Remove extension

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: "binary" });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
                setExcelData(data);
            } catch (err) {
                setError(
                    "Error reading Excel file. Please make sure it's a valid Excel file."
                );
                console.error(err);
            }
        };
        reader.readAsBinaryString(file);
    };

    const convertToPdf = async () => {
        if (excelData.length === 0) {
            setError("No Excel data to convert. Please upload a file first.");
            return;
        }

        setIsConverting(true);
        setError("");

        try {
            // Create a PDF with the table content
            const pdf = new jsPDF("landscape");

            // Convert table to image
            const dataUrl = await toPng(tableRef.current);

            // Add image to PDF
            const imgProps = pdf.getImageProperties(dataUrl);
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

            pdf.addImage(dataUrl, "PNG", 0, 0, pdfWidth, pdfHeight);

            // Save the PDF
            pdf.save(`${fileName || "converted"}.pdf`);
        } catch (err) {
            setError("Error generating PDF. Please try again.");
            console.error(err);
        } finally {
            setIsConverting(false);
        }
    };

    return (
        <>
            <HeaderSection toolId="excelToPdf" />
            <div className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4 dark:text-white">
                <div className="bg-white rounded-xl dark:bg-gray-800 shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
                        <h1 className="text-2xl font-bold mb-2 flex items-center">
                            <FontAwesomeIcon
                                icon={faFileExcel}
                                className="mr-3"
                            />
                            Excel to PDF Converter
                        </h1>
                        <p className="opacity-90">
                            Convert Excel files to PDF documents instantly
                        </p>
                    </div>

                    <div className="space-y-6 p-6 m-6">
                        <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-8 m-6">
                            <input
                                type="file"
                                id="excel-upload"
                                accept=".xlsx,.xls,.csv"
                                onChange={handleFileUpload}
                                className="hidden"
                            />
                            <label
                                htmlFor="excel-upload"
                                className="cursor-pointer bg-[#74D19C] hover:bg-[#74D19C] text-white font-medium py-2 px-4 rounded-md transition duration-200"
                            >
                                Upload Excel File
                            </label>
                            <p className="mt-2 text-sm text-gray-500">
                                Supports .xlsx, .xls, .csv files
                            </p>
                            {fileName && (
                                <p className="mt-2 text-sm text-gray-700">
                                    File:{" "}
                                    <span className="font-medium">
                                        {fileName}
                                    </span>
                                </p>
                            )}
                        </div>

                        {error && (
                            <div className="bg-red-50 border-l-4 border-red-500 p-4">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg
                                            className="h-5 w-5 text-red-500"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm text-red-700">
                                            {error}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {excelData.length > 0 && (
                            <div className="overflow-x-auto">
                                <div
                                    ref={tableRef}
                                    className="bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg"
                                >
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                {excelData[0]?.map(
                                                    (header, index) => (
                                                        <th
                                                            key={index}
                                                            scope="col"
                                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-400 dark:text-gray-400 uppercase tracking-wider"
                                                        >
                                                            {header ||
                                                                `Column ${
                                                                    index + 1
                                                                }`}
                                                        </th>
                                                    )
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 divide-y divide-gray-200">
                                            {excelData
                                                .slice(1)
                                                .map((row, rowIndex) => (
                                                    <tr key={rowIndex}>
                                                        {row.map(
                                                            (
                                                                cell,
                                                                cellIndex
                                                            ) => (
                                                                <td
                                                                    key={
                                                                        cellIndex
                                                                    }
                                                                    className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                                                                >
                                                                    {cell}
                                                                </td>
                                                            )
                                                        )}
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        <div className="flex justify-center">
                            <button
                                onClick={convertToPdf}
                                disabled={
                                    excelData.length === 0 || isConverting
                                }
                                className={`px-6 py-3 rounded-md w-[100%] m-6  text-white font-medium ${
                                    excelData.length === 0
                                        ? "bg-gray-400 cursor-not-allowed"
                                        : "bg-green-500 hover:bg-green-600"
                                } transition duration-200`}
                            >
                                {isConverting ? (
                                    <span className="flex items-center">
                                        <svg
                                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            ></path>
                                        </svg>
                                        Converting...
                                    </span>
                                ) : (
                                    "Convert to PDF"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <BottomSection toolId="excelToPdf" />
        </>
    );
};

export default ExcelToPdfConverter;
